import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionNumlineSlider from '~/bootcamp/Activities/Fractions/components/FractionNumlineSlider'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generateNumeratorDenominatorForNumline } from './helpers'

const MIN_NUMERATOR = [1, 0]
const MAX_NUMERATOR = [4, 10]
const MIN_DENOMINATOR = 2
const MAX_DENOMINATOR = 5

function UnitFractionNumline({
  answerState,
  onNextQuestionClick,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState<number>(0)
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [denominator, setDenominator] = useState(3)
  const [numerator, setNumerator] = useState(5)

  useEffect(() => {
    setAnswer(0)
    const numbers = generateNumeratorDenominatorForNumline(
      MIN_NUMERATOR[difficultyLevel - 1],
      MAX_NUMERATOR[difficultyLevel - 1],
      MIN_DENOMINATOR,
      MAX_DENOMINATOR,
      difficultyLevel,
      taskHistory,
    )
    setNumerator(numbers.numerator)
    setDenominator(numbers.denominator)
  }, [taskHistory, difficultyLevel])

  // Slotless: the slider position is the answer.
  useEmitInstance(onInstanceChange, `\\frac{${numerator}}{${denominator}}`)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer === numerator
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer(0)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [numerator, ...prevState].slice(0, 2)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="my-0 w-full">
        <div className="flex flex-col justify-center items-center mb-10">
          {numerator}
          <Divider className="w-5" />
          {denominator}
        </div>
        <FractionNumlineSlider
          aria-label={t('ariaLabelButtons.adjustSliderToMatchFraction')}
          danger={answerState === false}
          max={denominator * 2}
          min={0}
          setValue={setAnswer}
          value={answer}
        />
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default UnitFractionNumline
